import { useState } from "react";
import { LibraryBig } from "lucide-react";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList, Popover, PopoverContent, PopoverTrigger } from "@lumanity/ui/primitives";
import { useSectionPrompts } from "./draftApi";
import type { SectionPrompt } from "./types";

/**
 * The "choose from the prompt library" button beside AI instructions (Admin → Prompt Library).
 * The library's Command list does the searching (name and description) and keyboard navigation.
 */
const PromptPicker = ({ disabled, onPick }: { disabled: boolean; onPick: (prompt: SectionPrompt) => void }) => {
  const [open, setOpen] = useState(false);
  const prompts = useSectionPrompts({ enabled: open });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title="Choose from the prompt library"
          aria-label="Choose from the prompt library"
          className="shrink-0 self-start rounded border border-brand-teal p-1.5 text-brand-teal hover:bg-brand-teal/10 disabled:opacity-40"
        >
          <LibraryBig className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <Command>
          <CommandInput placeholder="Search prompts" aria-label="Search prompts" />
          <CommandList className="max-h-72">
            {prompts.isLoading && <p className="p-3 text-sm text-muted-foreground">Loading prompts...</p>}
            {prompts.error && <p className="p-3 text-sm text-destructive">{prompts.error.message}</p>}
            {prompts.data && <CommandEmpty>No prompts found.</CommandEmpty>}
            {(prompts.data ?? []).map((p) => (
              <CommandItem
                key={p.id}
                // Unique per prompt, searched with the name and description.
                value={p.id}
                keywords={[p.name, p.description ?? ""]}
                onSelect={() => {
                  onPick(p);
                  setOpen(false);
                }}
                className="flex-col items-start gap-0"
              >
                <span className="block text-sm font-medium">{p.name}</span>
                {p.description && <span className="block text-xs text-muted-foreground line-clamp-2">{p.description}</span>}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default PromptPicker;
